import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useMediaQuery
} from '@mui/material'
import { createTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'

// Icons
import CloseIcon from '../../../assets/icons/circles-design/Close.png'

import BankTransferForm from './BankTransferForm'
// Forms
import PaymentSelection from './PaymentSelection'
import StripeForm from './StripeForm'
import StripeQRForm from './StripeQRForm'

export default function PaymentModal({
  isDialogOpen,
  setDialogOpen,
  handleSubmit,
  amountPayable, // amount to pay
  orgId, // To identify billed paid
  email, // To notify customer through email
  setPaymentMethod,
  paymentMethod,
  PAYMENT_METHODS,
  handleNext,
  metaInfo,
  variant
}) {
  const [paymentPageNo, setPaymentPageNo] = useState(0)
  const ALLOWED_METHODS =
    variant === 'bill'
      ? [PAYMENT_METHODS.BANK_TRANSFER, PAYMENT_METHODS.PAYNOW]
      : [
          PAYMENT_METHODS.BANK_TRANSFER,
          PAYMENT_METHODS.CREDIT_CARD,
          PAYMENT_METHODS.PAYNOW
        ]

  // react breakpoint
  const theme = createTheme({
    breakpoints: {
      values: {
        mobile: 0,
        tablet: 640,
        laptop: 1024,
        desktop: 1200
      }
    }
  })
  const phoneScreen = useMediaQuery(theme.breakpoints.down('tablet'))

  // Handles next button in modal

  const modalGoNextPage = () => {
    setPaymentPageNo(paymentPageNo + 1)
  }

  const modalGoPrevPage = () => {
    setPaymentPageNo(paymentPageNo - 1)
    setPaymentMethod(ALLOWED_METHODS[0])
  }

  // Choose payment method

  const CHOOSE_PAYMENT = (
    <PaymentSelection
      PAYMENT_METHODS={PAYMENT_METHODS}
      ALLOWED_METHODS={ALLOWED_METHODS}
      setPaymentMethod={setPaymentMethod}
      variant={variant}
    />
  )

  // Update credit card page

  const PAYMENT_METHOD = (
    <>
      {paymentMethod === PAYMENT_METHODS.CREDIT_CARD && (
        <StripeForm
          orgId={orgId}
          email={email}
          amountPayable={amountPayable}
          setDialogOpen={setDialogOpen}
          handleNext={handleNext}
          handleBack={modalGoPrevPage}
          metaInfo={metaInfo}
          variant={variant}
        />
      )}
      {paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && (
        <BankTransferForm
          handleBack={modalGoPrevPage}
          handleSubmit={handleSubmit}
        />
      )}
      {paymentMethod === PAYMENT_METHODS.PAYNOW && (
        <StripeQRForm
          orgId={orgId}
          email={email}
          amountPayable={amountPayable}
          setDialogOpen={setDialogOpen}
          handleNext={handleNext}
          handleBack={modalGoPrevPage}
          metaInfo={metaInfo}
          variant={variant}
        />
      )}
    </>
  )

  // Page identifier

  const MODAL_PAGES = [CHOOSE_PAYMENT, PAYMENT_METHOD]
  const DIALOG_TITLES = ['Choose Payment Method', 'Payment Confirmation']

  return (
    <Dialog
      id='modal'
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minWidth: phoneScreen ? '300px' : '800px',
          minHeight: '571px' // Find a way to set it to size stated in ui
        }
      }}
      open={isDialogOpen}
      onClose={() => setDialogOpen(false)}
    >
      <DialogTitle sx={{ padding: '16px 38px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant='h5'>
            {paymentPageNo !== 1 ||
            paymentMethod === PAYMENT_METHODS.CREDIT_CARD ||
            paymentMethod === PAYMENT_METHODS.PAYNOW
              ? DIALOG_TITLES[paymentPageNo]
              : 'Bank Transfer'}
          </Typography>
          <IconButton
            aria-label='close'
            onClick={() => setDialogOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <img src={CloseIcon} width='24px' alt='Close Icon' />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />

      {/* DialogContent here that displays the correct form */}
      {MODAL_PAGES[paymentPageNo]}

      {/* Buttons for respective forms */}
      <DialogActions align='right'>
        {paymentPageNo === 0 && (
          <>
            <Button
              variant='secondary'
              onClick={() => {
                paymentPageNo > 0
                  ? setPaymentPageNo(paymentPageNo - 1)
                  : setDialogOpen(false)
              }}
              sx={{ marginRight: '10px' }}
              data-cy='backPaymentBtn'
            >
              Back
            </Button>
            <Button
              variant='filled'
              id='nextPaymentBtn'
              onClick={modalGoNextPage}
              sx={{ marginRight: '20px' }}
              data-cy='nextPaymentBtn'
            >
              Next
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

PaymentModal.propTypes = {
  isDialogOpen: PropTypes.bool,
  setDialogOpen: PropTypes.func,
  handleNext: PropTypes.func,
  amountPayable: PropTypes.number,
  paymentPageNo: PropTypes.number,
  setPaymentPageNo: PropTypes.func,
  orgId: PropTypes.string,
  email: PropTypes.string,
  handleSubmit: PropTypes.func,
  paymentMethod: PropTypes.string,
  setPaymentMethod: PropTypes.func,
  PAYMENT_METHODS: PropTypes.object,
  metaInfo: PropTypes.object,
  variant: PropTypes.string
}
